import React from 'react';
import { Alert, Button, useTheme } from '@mui/material';
import { type ActiveModelStatus } from '../../../../shared/api/grpc/revitClient';

export interface ModelStatusBannerProps {
  activeModelStatus: ActiveModelStatus | null;
  isModelWriteBlocked: boolean;
  modelBlockReason: 'no-document' | 'central-inaccessible' | 'read-only' | null;
  modelStatusDialogDismissed: boolean;
  setModelStatusDialogDismissed: (value: boolean) => void;
  refreshActiveModelStatus: () => void | Promise<void>;
}

export const ModelStatusBanner: React.FC<ModelStatusBannerProps> = ({
  activeModelStatus,
  isModelWriteBlocked,
  modelBlockReason,
  modelStatusDialogDismissed,
  setModelStatusDialogDismissed,
}) => {
  const theme = useTheme();

  if (!activeModelStatus || !activeModelStatus.success) {
    return null;
  }

  if (isModelWriteBlocked) {
    if (!modelStatusDialogDismissed) {
      return null;
    }

    return (
      <Alert
        severity="error"
        sx={{ mb: theme.spacing(2) }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setModelStatusDialogDismissed(false)}
          >
            Details
          </Button>
        }
      >
        {modelBlockReason === 'no-document' ? (
          <>No Revit document is open. Quick Fill is disabled.</>
        ) : modelBlockReason === 'central-inaccessible' ? (
          <>
            <strong>{activeModelStatus.documentTitle}</strong> is a workshared
            model whose central model is not accessible. Quick Fill is disabled
            until the model is re-saved as a reachable central. Click{' '}
            <strong>Details</strong> for step-by-step instructions.
          </>
        ) : (
          <>
            <strong>{activeModelStatus.documentTitle}</strong> is read-only.
            Quick Fill cannot write parameters until the model is reopened
            with write access.
          </>
        )}
      </Alert>
    );
  }

  if (activeModelStatus.isWorkshared && activeModelStatus.isDetached) {
    return (
      <Alert severity="warning" sx={{ mb: theme.spacing(2) }}>
        <strong>{activeModelStatus.documentTitle}</strong> is open as a detached
        copy. Parameter changes will not sync back to the central model. Reopen the
        project from central if you need to publish your changes.
      </Alert>
    );
  }

  if (
    activeModelStatus.isWorkshared &&
    activeModelStatus.centralModelPath &&
    !activeModelStatus.centralModelExists
  ) {
    return (
      <Alert severity="warning" sx={{ mb: theme.spacing(2) }}>
        <strong>{activeModelStatus.documentTitle}</strong> is workshared,
        but its central model file is missing at{' '}
        <code>{activeModelStatus.centralModelPath}</code>. Saving and syncing will
        fail until the central file is reachable.
      </Alert>
    );
  }

  return null;
};

export default ModelStatusBanner;
